import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import moment from "moment";

/** COMPONENT */
import { Box, Typography } from "@mui/material";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import AutocompleteSelect from "../../../component/select/autoCompleteSelect";
import InputTextField from "../../../component/input/inputTextField";
import InputDatePicker from "../../../component/input/inputDatePicker";
import Loading from "../../../component/loading/loading";
import { notiError, notiSuccess } from "../../../component/notification/notifications";
import ReclaimContractTable from "../components/ReclaimContractTable";

/** API */
import StallReclaimApi, {
  StallReclaimLease,
  StallReclaimOwner,
} from "../../../api/stall-reclaim/stall-reclaim.api";

/** UTILS */
import { colors } from "../../../constants/colors";
import {
  STALL_RECLAIM_NO_MAX_LENGTH,
  STALL_RECLAIM_REASON_MAX_LENGTH,
} from "../../../constants/stallReclaim";
import { getApiErrorMessage } from "../../../utils/api-error.utils";
import { getBranch } from "../../../utils/app.utils";
import { getDepositRefundError as getStallReclaimError } from "../../depositRefund/depositRefund.utils";

/** STYLE */
import * as Style from "../stallReclaim.style";

type Props = {
  onClose: () => void;
  /** สำเร็จแล้ว: parent ปิด modal + reload ตาราง ในจังหวะเดียว */
  onSuccess: () => void;
};

/** ดีไซน์แสดง suggestion 6 รายการ */
const OWNER_SUGGESTION_LIMIT = 6;

export default function ModalStallReclaimCreate({ onClose, onSuccess }: Props) {
  /** สาขาที่เลือกไว้ — ต้องส่งทุกเส้น ไม่งั้นค้นเจอผู้เช่าข้ามสาขา */
  const branchId = (() => {
    const value = Number(getBranch());
    return Number.isFinite(value) && value > 0 ? value : undefined;
  })();

  const [ownerOptions, setOwnerOptions] = useState<StallReclaimOwner[]>([]);
  const [isLoadingOwner, setIsLoadingOwner] = useState<boolean>(false);
  const [searchOwner, setSearchOwner] = useState<string>("");
  const [owner, setOwner] = useState<StallReclaimOwner | null>(null);

  const [leases, setLeases] = useState<StallReclaimLease[]>([]);
  const [isLoadingLease, setIsLoadingLease] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [reclaimNo, setReclaimNo] = useState<string>("");
  /** 'YYYY-MM-DD' — ค่าที่จะส่ง backend ไม่ใช่ข้อความ พ.ศ. ที่โชว์บนจอ */
  const [dueDate, setDueDate] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [touchedDueDate, setTouchedDueDate] = useState<boolean>(false);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  /** กันกดปุ่มรัวก่อน isSubmitting จะ re-render ทัน — 1 สัญญามีคำสั่งค้างได้ใบเดียว */
  const isSubmittingRef = useRef<boolean>(false);

  const loadOwnerOptions = useCallback(
    async (keyword: string) => {
      setIsLoadingOwner(true);
      try {
        const res = await StallReclaimApi.ownerSearch({
          keyword: keyword || undefined,
          branchId,
          // 1 = เฉพาะผู้เช่าที่มีสัญญาสถานะ "สัญญาเช่า" ให้เรียกคืนได้
          only_has_active_lease: 1,
          page: 1,
          limit: OWNER_SUGGESTION_LIMIT,
        });
        setOwnerOptions(res.data?.data?.items || []);
      } catch (error) {
        setOwnerOptions([]);
      } finally {
        setIsLoadingOwner(false);
      }
    },
    [branchId]
  );

  /** debounce 300ms กันยิง search ทุก keystroke */
  useEffect(() => {
    const timer = setTimeout(() => {
      loadOwnerOptions(searchOwner);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchOwner, loadOwnerOptions]);

  /** เลือกผู้เช่าแล้วโหลดสัญญาของคนนั้น */
  useEffect(() => {
    let isMounted = true;

    if (!owner?.userId) {
      setLeases([]);
      return;
    }

    const loadLeases = async () => {
      setIsLoadingLease(true);
      try {
        const res = await StallReclaimApi.ownerLease(owner.userId, branchId);
        if (!isMounted) return;
        setLeases(res.data?.data || []);
      } catch (error) {
        if (!isMounted) return;
        setLeases([]);
        notiError(getStallReclaimError(error, "ดึงรายการสัญญาเช่าของผู้เช่าไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoadingLease(false);
      }
    };

    loadLeases();
    return () => {
      isMounted = false;
    };
  }, [owner, branchId]);

  /** เปลี่ยนผู้เช่าต้องล้างสัญญาที่เลือกไว้ ไม่งั้นส่ง leaseAgreementId ของคนอื่นไป */
  const handleChangeOwner = (value: StallReclaimOwner | null) => {
    setOwner(value);
    setSelectedId(null);
  };

  const handleChangeDueDate = (value: any) => {
    // InputDatePicker โชว์เป็น พ.ศ. แต่ค่าที่ส่ง backend ต้องเป็น ค.ศ. ISO
    setDueDate(value ? moment(value).format("YYYY-MM-DD") : "");
    setTouchedDueDate(true);
  };

  /**
   * นับเฉพาะสัญญาที่ออกคำสั่งได้จริง — สัญญาที่มีคำสั่งค้างอยู่ยังแสดงในตาราง (แบบ disabled)
   * เพื่อให้เจ้าหน้าที่เห็นว่าสัญญาไม่ได้หายไปไหน แต่ไม่ควรถูกนับใน "เลือก x จาก N"
   */
  const selectableCount = leases.filter((lease) => lease.can_reclaim).length;

  const dueDateError = touchedDueDate && !dueDate ? "กรุณาระบุกำหนดวันคืนพื้นที่" : undefined;
  const isValid = !!owner?.userId && !!selectedId && !!dueDate;

  const onSubmit = async () => {
    setTouchedDueDate(true);
    if (!isValid || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      // ผู้เช่าอ้างด้วย userId ไม่ใช่ customerId — backend หา customerId เองจาก userId
      // UI เลือกได้ทีละสัญญา แต่ API รับเป็น array เสมอ จึงห่อ 1 ตัวส่งไป
      const res = await StallReclaimApi.create({
        userId_owner: owner!.userId,
        leaseAgreementIds: [selectedId!],
        deposit_refund_no: reclaimNo.trim() || undefined,
        due_date: dueDate,
        reason: reason.trim() || undefined,
      });

      const created = res.data?.data?.created || [];
      const skipped = res.data?.data?.skipped || [];

      // 201 ก็มีสิทธิ์ที่ใบนั้นถูกข้าม ต้องอ่าน skipped[] มาแจ้ง ไม่ใช่ขึ้นสำเร็จเฉยๆ
      if (created.length === 0) {
        notiError(skipped[0]?.reason || "สร้างคำสั่งเรียกคืนแผงค้าไม่สำเร็จ");
        return;
      }

      notiSuccess(
        `สร้างคำสั่งเรียกคืนแผงค้าสำเร็จ ${created[0]?.deposit_refund_no || ""}`.trim()
      );
      if (skipped.length > 0) notiError(skipped[0]?.reason || "");
      onSuccess();
    } catch (error) {
      // backend ตอบข้อความไทยมาแล้ว (ไม่พบสัญญา / มีคำสั่งค้าง / เลขที่คำสั่งซ้ำ) แสดงตรงๆ
      notiError(getApiErrorMessage(error, "สร้างคำสั่งเรียกคืนแผงค้าไม่สำเร็จ"));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const renderBody = () => (
    <Box
      className="custom-scroll"
      sx={{ px: { xs: 2, md: 3 }, pb: 1, maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}
    >
      <Row>
        <Col md={6} xs={12} className="my-2">
          <AutocompleteSelect
            labelId="stall-reclaim-owner"
            heading="ผู้เช่าแผงค้า"
            required
            placeholder="ค้นหาชื่อ หรือเลขประจำตัวประชาชน"
            options={ownerOptions}
            disabled={isSubmitting}
            getOptionLabel={(option: any) => option?.owner_fullname || ""}
            // ต้องระบุเอง ไม่งั้น client กรองผลที่ค้นด้วยเลขบัตร (ที่ server หาเจอ) ทิ้ง
            // เพราะ default stringify ของ wrapper ใช้ getOptionLabel = ชื่อ อย่างเดียว
            filterOptions={(option: any) =>
              `${option?.owner_fullname || ""} ${option?.idCard || ""}`
            }
            renderOption={(optionProps: any, option: any) => (
              <li {...optionProps} key={option?.userId}>
                <Box>
                  <Typography sx={{ fontSize: 16 }}>{option?.owner_fullname || "-"}</Typography>
                  {/* เลขบัตรถูก mask จาก backend มาแล้ว ห้าม mask ซ้ำ */}
                  <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
                    {`เลขประจำตัวประชาชน ${option?.idCard || "-"} · ${option?.lease_count ?? 0} สัญญา`}
                  </Typography>
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option: any, value: any) => option?.userId === value?.userId}
            inputValue={searchOwner || ""}
            onInputChange={(event: any, value: any) => setSearchOwner(value)}
            value={owner}
            onChange={(event: any, value: any) => handleChangeOwner(value)}
            loading={isLoadingOwner}
            noOptionsText="ไม่พบรายชื่อ"
          />
        </Col>
      </Row>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 2, mb: 1, flexWrap: "wrap" }}>
        <Style.SectionTitle>แผงค้าภายใต้สัญญาเช่า</Style.SectionTitle>
        {owner && !isLoadingLease && selectableCount > 0 && (
          <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>
            {`เลือก ${selectedId ? 1 : 0} จาก ${selectableCount} สัญญา`}
          </Typography>
        )}
      </Box>

      <ReclaimContractTable
        leases={leases}
        selectedId={selectedId}
        onSelect={(leaseAgreementId) => setSelectedId(leaseAgreementId)}
        disabled={isSubmitting}
        textEmptyData={
          !owner
            ? "ค้นหาและเลือกผู้เช่าแผงค้าเพื่อแสดงรายการแผงค้า"
            : isLoadingLease
            ? "กำลังโหลดข้อมูลสัญญาเช่า"
            : "ผู้เช่ารายนี้ไม่มีสัญญาเช่าที่เรียกคืนได้"
        }
      />

      <Row className="mt-3">
        <Col md={6} xs={12} className="my-1">
          <InputTextField
            heading="เลขที่คำสั่ง"
            value={reclaimNo}
            disabled={isSubmitting}
            placeholder="เช่น RCL-2569-0032"
            inputProps={{ maxLength: STALL_RECLAIM_NO_MAX_LENGTH }}
            // ไม่กรอก = ระบบออกเลขให้ · กรอกเลขที่ซ้ำกับใบอื่น backend จะตอบข้อความไทยมาให้
            helperText={undefined}
            onchange={(e: React.ChangeEvent<HTMLInputElement>) => setReclaimNo(e.target.value)}
          />
        </Col>
        <Col md={6} xs={12} className="my-1">
          <InputDatePicker
            heading="กำหนดวันคืนพื้นที่"
            required
            value={dueDate || null}
            onChange={handleChangeDueDate}
            onClear={() => handleChangeDueDate(null)}
            allowClear
            placeholder="เลือกวันที่"
            disabled={isSubmitting}
            helperText={dueDateError}
          />
        </Col>
      </Row>

      <Box sx={{ mt: 1 }}>
        <InputTextField
          heading="รายละเอียด"
          value={reason}
          multiline
          rows={4}
          maxRows={4}
          // multiline ต้องคู่กับ inputHeight 'auto' ไม่งั้นกล่องถูกล็อกความสูง 43px
          inputHeight={"auto"}
          disabled={isSubmitting}
          placeholder="ระบุรายละเอียดการค้างชำระ การผิดสัญญา หรือมติที่ประชุม"
          inputProps={{ maxLength: STALL_RECLAIM_REASON_MAX_LENGTH }}
          onchange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
        />
      </Box>
    </Box>
  );

  return (
    <>
      {(isSubmitting || isLoadingLease) && <Loading show />}
      <ModalCustom
        // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
        isShowProps
        // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
        autoResetModal={false}
        // popper ของ InputDatePicker render ผ่าน portal ออกนอก modal
        // ถ้า enforceFocus react-bootstrap จะแย่ง focus กลับ ปฏิทินเลือกวันไม่ได้
        enforceFocus={false}
        size="xl"
        closeButton
        alignFooter="end"
        footerInline
        title="สร้างคำสั่งเรียกคืนแผงค้า"
        component={renderBody()}
        textBtnCancel="ยกเลิก"
        onReset={onClose}
        textBtnConfirm="ยืนยัน"
        onSubmit={onSubmit}
        disabledSubmit={!isValid || isSubmitting}
        onClose={onClose}
      />
    </>
  );
}
